import {
  Component,
  Input,
  ElementRef,
  ChangeDetectorRef,
  AfterViewInit,
  OnDestroy,
  Inject,
  PLATFORM_ID
} from '@angular/core';

import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';

import { CartService } from '../../services/cart';
import { LanguageService } from '../../services/language.service';
import { BnNumberPipe } from '../../pipes/bn-number.pipe';


// হোমপেজের "হট ডিলস / রেকমেন্ডেড / ট্রেন্ডিং" সেকশন — Sindabad-এর মতো।
// প্রথমে `rows` সারি (ডিফল্ট ২) দেখায় — প্রতি সারিতে কয়টা কার্ড, সেটা পর্দার মাপ দেখে নিজে ঠিক হয়,
// তাই সারি সবসময় পুরো ভরা থাকে (শেষ সারি অর্ধেক ফাঁকা থাকে না)।
// seeAllLink থাকলে "সব দেখুন" সেই পেজে নিয়ে যায়, না থাকলে একই জায়গায় বাকি প্রোডাক্ট খুলে দেয়।
@Component({
  selector: 'app-product-section',
  standalone: true,
  imports: [CommonModule, RouterModule, BnNumberPipe],
  templateUrl: './product-section.html',
  styleUrls: ['./product-section.css']
})
export class ProductSection implements AfterViewInit, OnDestroy {

  @Input() titleBn = '';
  @Input() titleEn = '';

  @Input() products: any[] = [];

  // যেমন '/offers' — না দিলে "সব দেখুন" একই জায়গায় প্রসারিত হয়
  @Input() seeAllLink = '';

  // প্রথমে কয় সারি দেখাবে (অ্যাডমিন → হোমপেজ ডিজাইন থেকে বদলায়)
  @Input() rows = 2;

  expanded = false;

  // এক সারিতে কয়টা কার্ড — হোস্টের চওড়া দেখে ঠিক হয়
  cols = 5;

  private ro?: ResizeObserver;

  // কার্ডের সর্বনিম্ন চওড়া (px) আর কার্ডের মাঝের ফাঁক
  private readonly MIN_CARD = 190;
  private readonly GAP = 16;

  readonly apiUrl =
    'https://superbangladesh-api-1.onrender.com';

  constructor(
    private router: Router,
    public cart: CartService,
    public languageService: LanguageService,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngAfterViewInit(): void {

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.measure();

    if (typeof ResizeObserver !== 'undefined') {

      this.ro = new ResizeObserver(() => this.measure());

      this.ro.observe(this.host.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.ro?.disconnect();
  }

  private measure(): void {

    const w = this.host.nativeElement.clientWidth;

    if (!w) {
      return;
    }

    // মোবাইলে সবসময় ২টা
    const next = w < 520
      ? 2
      : Math.max(2, Math.min(6, Math.floor((w + this.GAP) / (this.MIN_CARD + this.GAP))));

    if (next !== this.cols) {
      this.cols = next;
      this.cdr.detectChanges();
    }
  }

  get initialCount(): number {
    return Math.max(1, Math.floor(this.rows || 2)) * this.cols;
  }

  get isBn(): boolean {
    return this.languageService.language() === 'bn';
  }

  get title(): string {
    return this.isBn ? (this.titleBn || this.titleEn) : (this.titleEn || this.titleBn);
  }

  get visible(): any[] {
    return this.expanded
      ? this.products
      : this.products.slice(0, this.initialCount);
  }

  get canExpand(): boolean {
    return !this.seeAllLink
      && !this.expanded
      && this.products.length > this.initialCount;
  }

  t(key: string): string {
    return this.languageService.translate(key);
  }

  expand(): void {
    this.expanded = true;
  }

  getFinalPrice(p: any): number {

    const price = Number(p?.price) || 0;
    const discount = Number(p?.discount) || 0;

    if (!discount) {
      return price;
    }

    return Math.round(price - (price * discount / 100));
  }

  getImage(url: string): string {

    if (!url) {
      return 'assets/no-image.png';
    }

    if (url.startsWith('http')) {
      return url;
    }

    if (url.startsWith('/uploads')) {
      return `${this.apiUrl}${url}`;
    }

    return `${this.apiUrl}/images/${url}`;
  }

  onImgError(event: Event): void {

    const img = event.target as HTMLImageElement;

    if (img.src.includes('no-image.png')) {
      return;
    }

    img.src = 'assets/no-image.png';
  }

  openProduct(id: number): void {

    if (!id) {
      return;
    }

    this.router.navigate(['/product', id]);
  }

  qty(p: any): number {
    return this.cart.getQty(p?.id);
  }

  add(p: any, event?: Event): void {

    event?.preventDefault();
    event?.stopPropagation();

    if (p.stock !== undefined && p.stock !== null && Number(p.stock) <= 0) {
      return;
    }

    this.cart.add({
      ...p,
      price: this.getFinalPrice(p)
    });
  }

  increase(p: any, event?: Event): void {

    event?.stopPropagation();

    this.cart.increaseByProduct({
      ...p,
      price: this.getFinalPrice(p)
    });
  }

  decrease(p: any, event?: Event): void {

    event?.stopPropagation();

    this.cart.decreaseByProduct(p);
  }

  trackByProduct(index: number, p: any): any {
    return p?.id ?? index;
  }
}
